import type { EntityType } from '../../../@types/rules';
import type {
    InterferenceVisualizationDraft,
    VisualizationProvider,
    VisualizationSequence,
} from './types';

function parseOptionalNumber(value: string): { value: number | null; valid: boolean } {
    if (value.trim() === '') return { value: null, valid: true };
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0
        ? { value: parsed, valid: true }
        : { value: null, valid: false };
}

const ENTITY_DISPLAY_NAMES: Partial<Record<EntityType, string>> = {
    ArchitecturalWall: 'Architectural Wall',
    StructuralColumn: 'Structural Column',
    StructuralWall: 'Structural Wall',
};

export function formatEntityName(entity: EntityType): string {
    return ENTITY_DISPLAY_NAMES[entity] ?? entity;
}

export function buildInterferenceVisualizationSequence(
    draft: InterferenceVisualizationDraft,
): VisualizationSequence {
    const clearance = parseOptionalNumber(draft.minClearanceCm);
    const tolerance = parseOptionalNumber(draft.overlapToleranceCm3);
    const key = JSON.stringify(draft);

    if (!draft.firstEntity || !draft.secondEntity) {
        return {
            key,
            ready: false,
            prompt: 'Select two entities to preview how the rule will be evaluated.',
            clearanceCm: clearance.value,
            overlapToleranceCm3: tolerance.value,
            summary: 'The visualization will start after both entities are selected.',
            scenarios: [],
        };
    }

    const firstName = formatEntityName(draft.firstEntity);
    const secondName = formatEntityName(draft.secondEntity);

    if (!clearance.valid) {
        return {
            key,
            ready: false,
            prompt: 'Enter a valid non-negative minimum clearance to preview the rule.',
            firstEntity: draft.firstEntity,
            secondEntity: draft.secondEntity,
            clearanceCm: null,
            overlapToleranceCm3: tolerance.value,
            summary: `${firstName} compared with ${secondName}.`,
            scenarios: [],
        };
    }

    if (clearance.value === null && !draft.touchingEnabled) {
        return {
            key,
            ready: false,
            prompt: 'Enable Touching or enter a minimum clearance to preview the rule.',
            firstEntity: draft.firstEntity,
            secondEntity: draft.secondEntity,
            clearanceCm: null,
            overlapToleranceCm3: tolerance.value,
            summary: `${firstName} compared with ${secondName}.`,
            scenarios: [],
        };
    }

    const hasClearanceZone = clearance.value !== null && clearance.value > 0;
    const overlapDescription = tolerance.valid && tolerance.value !== null
        ? `The illustrated overlap exceeds the configured ${tolerance.value} cm³ tolerance.`
        : `${firstName} physically intersects ${secondName}.`;

    const scenarios = [
        {
            id: 'allowed' as const,
            title: 'Allowed',
            description: hasClearanceZone
                ? `${firstName} remains outside the ${clearance.value} cm clearance zone.`
                : `${firstName} remains separated from ${secondName}.`,
            tone: 'success' as const,
        },
        ...(hasClearanceZone ? [{
            id: 'clearanceViolation' as const,
            title: 'Clearance Violation',
            description: `${firstName} enters the required ${clearance.value} cm clearance zone without touching ${secondName}.`,
            tone: 'warning' as const,
        }] : []),
        {
            id: 'clash' as const,
            title: 'Physical Clash',
            description: overlapDescription,
            tone: 'danger' as const,
        },
    ];

    return {
        key,
        ready: true,
        firstEntity: draft.firstEntity,
        secondEntity: draft.secondEntity,
        clearanceCm: clearance.value,
        overlapToleranceCm3: tolerance.valid ? tolerance.value : null,
        summary: hasClearanceZone
            ? `${firstName} must stay at least ${clearance.value} cm from ${secondName}.`
            : `${firstName} must remain separated from ${secondName}.`,
        scenarios,
    };
}

export const interferenceVisualizationProvider: VisualizationProvider<InterferenceVisualizationDraft> = {
    buildSequence: buildInterferenceVisualizationSequence,
};
